// =============================================================================
// io/share/shares.ts — writing, listing, revoking and publicly reading a share.
// -----------------------------------------------------------------------------
// The table and the Edge Function already exist and are LIVE, serving the old
// client: `shared_reports` (supabase/migrations/20260713120000_shared_reports.sql
// plus the expiry/revoke migration) and `supabase/functions/shared-report`. This
// client writes the same rows and reads through the same function, so a link
// made in either app opens in either app.
//
// ── 🚨 ANON NEVER TOUCHES THE TABLE ──
//
// There is no public `select` policy, deliberately: one would leak every user's
// payload. The public read goes through the Edge Function under the service
// role, which returns ONLY the row matching the exact token — so tokens are not
// enumerable, and revoked and expired links are answered by a gate that a
// PostgREST filter could not express.
//
// ⚠ The owner's own reads and the revoke go through PostgREST as usual, because
// RLS covers them: the whole policy is "a user may do anything to rows whose
// user_id is theirs".
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';
import { isShareKind, type ShareKind, type SharePayload } from '../../domain/share';

export interface CreateShareArgs {
  token: string;
  userId: string;
  kind: ShareKind;
  payload: SharePayload;
  /** ISO instant, or null for a link that never expires. */
  expiresAt: string | null;
}

export async function createShare(sb: SupabaseClient, a: CreateShareArgs): Promise<void> {
  const { error } = await sb.from('shared_reports').insert({
    token: a.token,
    user_id: a.userId,
    kind: a.kind,
    payload: a.payload,
    expires_at: a.expiresAt,
  });
  if (error) throw new Error(error.message);
}

/** One row of the owner's management list. ⚠ The token is NOT rendered — see below. */
export interface ShareLinkRow {
  token: string;
  kind: string;
  title: string;
  subtitle: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
}

/**
 * The owner's links, newest first.
 *
 * ⚠ REVOKED AND EXPIRED ONES COME BACK TOO. A list that hid them would answer
 * "is that link I sent in March still live?" with silence — which is the
 * question the list exists to answer.
 */
export async function loadShareLinks(sb: SupabaseClient): Promise<ShareLinkRow[]> {
  const { data, error } = await sb.from('shared_reports')
    .select('token, kind, payload, created_at, expires_at, revoked_at')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);

  return (data ?? []).map((r) => {
    const p = (r as { payload?: Partial<SharePayload> | null }).payload ?? {};
    const row = r as Record<string, unknown>;
    return {
      token: String(row['token'] ?? ''),
      kind: String(row['kind'] ?? ''),
      title: typeof p.title === 'string' && p.title ? p.title : String(row['kind'] ?? 'Report'),
      subtitle: typeof p.subtitle === 'string' ? p.subtitle : '',
      createdAt: String(row['created_at'] ?? ''),
      expiresAt: (row['expires_at'] as string | null) ?? null,
      revokedAt: (row['revoked_at'] as string | null) ?? null,
    };
  });
}

/**
 * Kill a link.
 *
 * ⚠ THE ROW IS KEPT, NOT DELETED. A deleted row makes the visitor's page say
 * "not found", which reads as our bug and invites them to ask for a re-send of
 * something that was deliberately killed. `revoked_at` is what lets the Edge
 * Function say "the owner took this back" instead.
 */
export async function revokeShare(sb: SupabaseClient, token: string): Promise<void> {
  const { error } = await sb.from('shared_reports')
    .update({ revoked_at: new Date().toISOString() })
    .eq('token', token);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// The public read
// ---------------------------------------------------------------------------

/** Why a report could not be shown. Three states, never collapsed into one. */
export type SharedReportError = 'not-found' | 'revoked' | 'expired' | 'failed';

export class SharedReportUnavailable extends Error {
  readonly reason: SharedReportError;
  constructor(reason: SharedReportError, message: string) {
    super(message);
    this.name = 'SharedReportUnavailable';
    this.reason = reason;
  }
}

export interface FetchedShare {
  kind: ShareKind | null;
  /** ⚠ UNTRUSTED. Every field is attacker-controllable — see ui/share. */
  payload: Partial<SharePayload>;
}

/**
 * Fetch a shared report as an anonymous visitor.
 *
 * ⚠ NO SUPABASE CLIENT. The visitor has no session and must not get one: calling
 * this through the app's client would attach whatever session happens to be in
 * this browser's storage, which on a shared machine means the report is read as
 * somebody else. A bare `fetch` with the anon key is the whole requirement.
 *
 * ⚠ THREE DISTINCT ANSWERS, because they mean different things to the person
 * holding the link. "Revoked" and "expired" are deliberate acts by whoever sent
 * it; "not found" is a bad URL. Telling someone a deliberately-killed link is
 * "not found" reads as our bug rather than as the owner's decision.
 */
export async function fetchSharedReport(token: string): Promise<FetchedShare> {
  const url = `${ENV.config.supabaseUrl}/functions/v1/shared-report`
    + `?token=${encodeURIComponent(token)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      headers: {
        apikey: ENV.config.supabaseAnonKey,
        Authorization: `Bearer ${ENV.config.supabaseAnonKey}`,
      },
    });
  } catch {
    throw new SharedReportUnavailable(
      'failed',
      'This shared report could not be loaded. Check your connection and try again.',
    );
  }

  if (!res.ok) {
    if (res.status === 404) {
      throw new SharedReportUnavailable(
        'not-found',
        'This shared report was not found. Check the link is complete.',
      );
    }
    if (res.status === 410) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      if (body?.error === 'expired') {
        throw new SharedReportUnavailable(
          'expired',
          'This shared report has expired. Ask whoever sent it for a fresh link.',
        );
      }
      throw new SharedReportUnavailable(
        'revoked',
        'This shared report was revoked by its owner and is no longer available.',
      );
    }
    throw new SharedReportUnavailable('failed', 'This shared report could not be loaded.');
  }

  const body = await res.json().catch(() => null) as
    { kind?: unknown; payload?: unknown } | null;
  if (!body) {
    throw new SharedReportUnavailable('failed', 'This shared report could not be read.');
  }
  return {
    // ⚠ Guarded rather than cast: `kind` decides which CSS scope the frame is
    // given, and it arrives from the network.
    kind: isShareKind(body.kind) ? body.kind : null,
    payload: (body.payload && typeof body.payload === 'object'
      ? body.payload : {}) as Partial<SharePayload>,
  };
}

/**
 * The URL a share link is handed out as.
 *
 * ⚠ `origin + pathname`, never the full href: the current page may already carry
 * a query string, and appending to it produces a link that opens the report AND
 * re-applies whatever filter the sharer had on.
 */
export function shareLinkFor(token: string, loc: { origin: string; pathname: string }): string {
  return `${loc.origin}${loc.pathname}?share=${token}`;
}

/** The query parameter the public view is keyed on. */
export const SHARE_PARAM = 'share';

/**
 * The token on the current URL, or null.
 *
 * ⚠ SHAPE-CHECKED HERE, not only on the server. The token goes into a URL that
 * is fetched, and a malformed one should never reach the network at all — the
 * Edge Function rejects the same shapes, and agreeing with it is cheaper than
 * a round trip that returns 400.
 */
export function shareTokenFrom(search: string): string | null {
  const raw = new URLSearchParams(search).get(SHARE_PARAM);
  if (!raw) return null;
  const t = raw.trim();
  if (t.length < 16 || t.length > 128 || !/^[a-zA-Z0-9]+$/.test(t)) return null;
  return t;
}
